/* eslint-disable indent */
import React, { useEffect, useState } from "react";
import { useDispatch, connect } from "react-redux";

import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  CardHeader,
  Spinner,
} from "reactstrap";

// eslint-disable-next-line object-curly-newline
import { Table, Thead, Tbody, Tr, Th, Td } from "react-super-responsive-table";
import "react-super-responsive-table/dist/SuperResponsiveTableStyle.css";

import { getSalesAgentsStart } from "store/users/actions";

import { MetaTags } from "react-meta-tags";

import ReportForm from "./ReportForm";
import { WalletReportSummary } from "./WalletReportSummary";
import TableLoader from "components/Common/TableLoader";
import formatDate, { formatDateBroker } from "helpers/formatDate";
import { Link } from "react-router-dom/cjs/react-router-dom.min";
import * as axiosHelper from "../../apis/api_helper";
import { captilazeFirstLetter } from "common/utils/manipulateString";
import Badge from "components/Common/Badge";
import { useTranslation } from "react-i18next";
import {
  getBaseCurrency,
  getRowCurrency,
} from "common/utils/FixedAmount";
import { fetchTradingAccounts } from "store/actions";
import { fetchClientsStart } from "store/client/actions";

const formatReportDate = (date) => formatDate(date, "DD/MM/YYYY hh:mm:ss A");

// Broker / MT5 server time (UTC+3). Defined once in helpers/formatDate.js
// (`formatDateBroker` / `REPORT_TZ`) and shared with the Client Profile
// transactions views so every transactional date renders in broker time,
// independent of the operator's machine timezone.

const getDecimalValue = (value) => {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "object" && value?.$numberDecimal !== undefined) {
    return Number(value.$numberDecimal);
  }

  const numericValue = Number(value);
  return Number.isNaN(numericValue) ? null : numericValue;
};

const formatAmountValue = (value) => {
  const amount = getDecimalValue(value);
  return amount === null ? "-" : amount.toFixed(2);
};

const formatReportDateBrokerField = (value) => (value?.date || value?.updatedAt
  ? formatDateBroker(value?.date || value?.updatedAt)
  : "-");

const formatSignedMoney = (value, polarity) => {
  const n = getDecimalValue(value);
  if (n === null) return "-";
  if (n === 0) return "0.00";
  const abs = Math.abs(n).toFixed(2);
  if (polarity === "negative") {
    return <span className="text-danger">-{abs}</span>;
  }
  return <span className="text-success">+{abs}</span>;
};
const formatActualSignedMoney = (value) => {
  const n = getDecimalValue(value);
  if (n === null) return "-";
  if (n === 0) return "0.00";
  const cls = n > 0 ? "text-success" : "text-danger";
  return <span className={cls}>{`${n > 0 ? "+" : ""}${n.toFixed(2)}`}</span>;
};

function getColumns(reportType, t) {
  switch (reportType) {
    case "deposit-status-fx":
      return [
        {
          dataField: "recordId",
          text: t("Record Id"),
        },
        {
          dataField: "date",
          text: t("Date"),
          formatter: (val) => formatReportDateBrokerField(val),
        },
        {
          dataField: "clientType",
          text: t("Client Type"),
          formatter: () => t("Individual"),
        },
        {
          dataField: "customerId",
          text: t("Name"),
          formatter: (val) => {
            return (
              <div>
                <i className="no-italics">
                  {" "}
                  {val.customerId
                    ? `${captilazeFirstLetter(
                      val.customerId?.firstName
                    )} ${captilazeFirstLetter(val.customerId?.lastName)}`
                    : ""}
                </i>
              </div>
            );
          },
        },
        {
          dataField: "masterIb",
          text: t("Master IB"),
          formatter: (val) => val?.masterIb ?? val?.masterIB ?? val?.[ "masterIb"] ?? "-",
        },
        {
          dataField: "gateway",
          text: t("Gateway"),
          formatter: (val) =>
            val.gateway == "WIRE_TRANSFER"
              ? "Wire"
              : val.gateway?.split("_").join(" ")?.toLowerCase(),
        },
        {
          dataField: "currency",
          text: t("Currency"),
          formatter: (val) => getRowCurrency(val),
        },
        {
          dataField: "amount",
          text: t("Amount"),
          formatter: (val) => formatAmountValue(val?.amount),
        },
        {
          dataField: "currency",
          text: t("Base Currency"),
          formatter: (val) => getBaseCurrency(val),
        },
        {
          dataField: "reason",
          text: t("Reason"),
          formatter: (val) =>
            val.reason ? (
              <div data-title={val.reason}>
                {val.reason.length > 20
                  ? `${val.reason.slice(0, 20)}...`
                  : val.reason}
              </div>
            ) : (
              <>{val?.note}</>
            ),
        },
        {
          dataField: "status",
          text: t("Status"),
          formatter: (val) => <Badge status={val.status}></Badge>,
        },
      ];
    case "withdrawals-status-fx":
      return [
        {
          dataField: "recordId",
          text: t("Transaction Id"),
        },
        {
          dataField: "date",
          text: t("Date"),
          formatter: (val) => formatReportDateBrokerField(val),
        },
        {
          dataField: "clientType",
          text: t("Client Type"),
          formatter: () => t("Individual"),
        },
        {
          dataField: "customerId",
          text: t("Customer Name"),
          formatter: (val) => {
            return (
              <div>
                <i className="no-italics">
                  {" "}
                  {val.customerId
                    ? `${captilazeFirstLetter(
                      val.customerId.firstName
                    )} ${captilazeFirstLetter(val.customerId.lastName)}`
                    : ""}
                </i>
              </div>
            );
          },
        },
        {
          dataField: "masterIb",
          text: t("Master IB"),
          formatter: (val) => val?.masterIb ?? val?.masterIB ?? "-",
        },
        {
          dataField: "gateway",
          text: t("Gateway"),
          formatter: (val) =>
            val.gateway == "WIRE_TRANSFER"
              ? "Wire"
              : val.gateway?.split("_").join(" ")?.toLowerCase(),
        },
        {
          dataField: "currency",
          text: t("Currency"),
          formatter: (val) => getRowCurrency(val),
        },
        {
          dataField: "amount",
          text: t("Amount"),
          formatter: (val) => formatAmountValue(val?.amount),
        },
        {
          dataField: "currency",
          text: t("Base Currency"),
          formatter: (val) => getBaseCurrency(val),
        },
        {
          dataField: "reason",
          text: t("Reason"),
          formatter: (val) =>
            val.reason ? (
              <div data-title={val.reason}>
                {val.reason.length > 20
                  ? `${val.reason.slice(0, 20)}...`
                  : val.reason}
              </div>
            ) : (
              <>{val?.note}</>
            ),
        },
        {
          dataField: "status",
          text: t("Status"),
          formatter: (val) => <Badge status={val.status}></Badge>,
        },
      ];
    case "deposit-status":
      return [
        {
          dataField: "recordId",
          text: t("Record Id"),
        },
        {
          dataField: "date",
          text: t("Date"),
          formatter: (val) => formatReportDateBrokerField(val),
        },
        {
          dataField: "clientType",
          text: t("Client Type"),
          formatter: () => t("Individual"),
        },
        {
          dataField: "customerId",
          text: t("Customer Name"),
          formatter: (val) => {
            return (
              <div>
                <i className="no-italics">
                  {" "}
                  {val.customerId
                    ? `${captilazeFirstLetter(
                      val.customerId?.firstName
                    )} ${captilazeFirstLetter(val.customerId?.lastName)}`
                    : ""}
                </i>
              </div>
            );
          },
        },
        {
          dataField: "masterIb",
          text: t("Master IB"),
          formatter: (val) => val?.masterIb ?? val?.masterIB ?? "-",
        },
        {
          dataField: "gateway",
          text: t("Gateway"),
          formatter: (val) =>
            val.gateway == "WIRE_TRANSFER"
              ? "Wire"
              : val.gateway?.split("_").join(" ")?.toLowerCase(),
        },
        {
          dataField: "currency",
          text: t("Currency"),
          formatter: (val) => getRowCurrency(val),
        },
        {
          dataField: "amount",
          text: t("Amount"),
          formatter: (val) => formatAmountValue(val?.amount),
        },
        {
          dataField: "currency",
          text: t("Base Currency"),
          formatter: (val) => getBaseCurrency(val),
        },
        {
          dataField: "reason",
          text: t("Reason"),
          formatter: (val) =>
            val.reason ? (
              <div data-title={val.reason}>
                {val.reason.length > 20
                  ? `${val.reason.slice(0, 20)}...`
                  : val.reason}
              </div>
            ) : (
              <>{val?.note}</>
            ),
        },
        {
          dataField: "status",
          text: t("Status"),
          formatter: (val) => <Badge status={val.status}></Badge>,
        },
      ];
    case "withdrawals-status":
      return [
        {
          dataField: "recordId",
          text: t("Transaction Id"),
        },
        {
          dataField: "date",
          text: t("Date"),
          formatter: (val) => formatReportDateBrokerField(val),
        },
        {
          dataField: "clientType",
          text: t("Client Type"),
          formatter: () => t("Individual"),
        },
        {
          dataField: "customerId",
          text: t("Customer Name"),
          formatter: (val) => {
            return (
              <div>
                <i className="no-italics">
                  {" "}
                  {val.customerId
                    ? `${captilazeFirstLetter(
                      val.customerId.firstName
                    )} ${captilazeFirstLetter(val.customerId.lastName)}`
                    : ""}
                </i>
              </div>
            );
          },
        },
        {
          dataField: "masterIb",
          text: t("Master IB"),
          formatter: (val) => val?.masterIb ?? val?.masterIB ?? "-",
        },
        {
          dataField: "gateway",
          text: t("Gateway"),
          formatter: (val) =>
            val.gateway == "WIRE_TRANSFER"
              ? "Wire"
              : val.gateway?.split("_").join(" ")?.toLowerCase(),
        },
        {
          dataField: "currency",
          text: t("Currency"),
          formatter: (val) => getRowCurrency(val),
        },
        {
          dataField: "amount",
          text: t("Amount"),
          formatter: (val) => formatAmountValue(val?.amount),
        },
        {
          dataField: "currency",
          text: t("Base Currency"),
          formatter: (val) => getBaseCurrency(val),
        },
        {
          dataField: "reason",
          text: t("Reason"),
          formatter: (val) =>
            val.reason ? (
              <div data-title={val.reason}>
                {val.reason.length > 20
                  ? `${val.reason.slice(0, 20)}...`
                  : val.reason}
              </div>
            ) : (
              <>{val?.note}</>
            ),
        },
        {
          dataField: "status",
          text: t("Status"),
          formatter: (val) => <Badge status={val.status}></Badge>,
        },
      ];
    case "client-deposits":
    case "client-withdrawals":
      return [
        {
          dataField: "updatedAt",
          text: t("Date"),
          formatter: (val) => formatDateBroker(val.updatedAt || val.createdAt),
        },
        {
          dataField: "clientType",
          text: t("Client Type"),
          formatter: () => t("Individual"),
        },
        {
          dataField: "firstName",
          text: t("Customer Name"),
          formatter: (val) =>
            val?.firstName
              ? (
                <Link
                  to={`/clients/${val?.customerId}/profile`}
                >{`${val?.firstName} ${val?.lastName}`}</Link>
              ) || "-"
              : "-",
        },
        {
          dataField: "masterIb",
          text: t("Master IB"),
          formatter: (val) => val?.masterIb ?? val?.masterIB ?? "-",
        },
        {
          dataField: "transactionType",
          text: t("Type"),
          formatter: (val) => val?.transactionType || "-",
        },
        {
          dataField: "amount",
          text: t("Amount"),
          formatter: (val) => formatAmountValue(val?.amount),
        },
        {
          dataField: "currency",
          text: t("Currency"),
          formatter: (val) => getRowCurrency(val),
        },
        {
          dataField: "gateway",
          text: t("Gateway"),
          formatter: (val) =>
            val.gateway == "WIRE_TRANSFER"
              ? "Wire"
              : val.gateway?.split("_").join(" ")?.toLowerCase(),
        },
        {
          dataField: "status",
          text: t("Status"),
          formatter: (val) => <Badge status={val.status}></Badge>,
        },
      ];
    case "daily-net-deposit":
      return [
        {
          dataField: "name",
          text: t("Customer Name"),
          formatter: (val) =>
            val?.firstName
              ? (
                <Link
                  to={`/clients/${val?._id}/profile`}
                >{`${val?.firstName} ${val?.lastName}`}</Link>
              ) || "-"
              : "-",
        },
        {
          dataField: "agent",
          text: t("Agent"),
          formatter: (val) =>
            val?.agent ? `${val.agent.firstName} ${val.agent.lastName}` : "-",
        },
        {
          dataField: "fxDeposit",
          text: t("FX Deposits"),
          formatter: (val) => <span className="color-green">{val?.fxDeposit ?? "-"}</span>
        },
        {
          dataField: "walletDeposit",
          text: t("Wallet Deposits"),
          formatter: (val) => <span className="color-green">{val?.walletDeposit ?? "-"}</span>
        },
        {
          dataField: "fxWithdraw",
          text: t("FX Withdrawals"),
          formatter: (val) => <span className="color-red">{val?.fxWithdraw ?? "-"}</span>
        },
        {
          dataField: "walletWithdraw",
          text: t("Wallet Withdrawals"),
          formatter: (val) => <span className="color-red">{val?.walletWithdraw ?? "-"}</span>
        },
        {
          dataField: "totalDeposits",
          text: t("Total Deposited"),
          formatter: (val) => <span className="color-green">{val?.totalDeposits ?? "-"}</span>
        },
        {
          dataField: "totalWithdrawals",
          text: t("Total Withdrawn"),
          formatter: (val) => <span className="color-red">{val?.totalWithdrawals ?? "-"}</span>
        },
        {
          dataField: "netProfit",
          text: t("Net Money Flow"),
          formatter: (val) => <span className={
            parseFloat(val?.netProfit) > 0 ? "color-green" : "color-red"
          }>{val?.netProfit ?? "-"}</span>
        },
        {
          dataField: "currency",
          text: t("Currency"),
          formatter: (val) => getRowCurrency(val),
        },
      ];
    case "new-client-deposits":
      return [
        {
          dataField: "name",
          text: t("Customer Name"),
          formatter: (val) =>
            val?.firstName
              ? (
                <Link
                  to={`/clients/${val?._id}/profile`}
                >{`${val?.firstName} ${val?.lastName}`}</Link>
              ) || "-"
              : "-",
        },
        {
          dataField: "agent",
          text: t("Agent"),
          formatter: (val) =>
            val?.agent ? `${val.agent.firstName} ${val.agent.lastName}` : "-",
        },
        {
          dataField: "fxSum",
          text: t("First Transaction Date"),
          formatter: (val) => formatReportDate(val?.firstTransaction?.createdAt),
        },
        {
          dataField: "firstTransaction",
          text: t("First Transaction Amount"),
          formatter: (val) => formatAmountValue(val?.firstTransaction?.amount),
        },
        {
          dataField: "createdAt",
          text: t("Client Registration Date"),
          formatter: (val) => formatReportDate(val.createdAt)
        },
      ];
    case "withdrawals":
    case "deposits":
      return [
        {
          dataField: "createdAt",
          text: t("Added in DB"),
          formatter: (val) => formatReportDate(val.createdAt),
        },
        {
          dataField: "clientType",
          text: t("Client Type"),
          formatter: () => t("Individual"),
        },
        {
          dataField: "customerName",
          text: t("Customer Name"),
          formatter: (val) =>
            val?.customer
              ? (
                <Link
                  to={`/clients/${val?.customer?._id}/profile`}
                >{`${val.customer.firstName} ${val.customer.lastName}`}</Link>
              ) || "-"
              : "-",
        },
        {
          dataField: "masterIb",
          text: t("Master IB"),
          formatter: (val) => val?.masterIb ?? val?.masterIB ?? "-",
        },
        {
          dataField: "login",
          text: t("AccountLogin"),
        },
        {
          dataField: "amount",
          text: t("Amount"),
          formatter: (val) => formatAmountValue(val?.amount),
        },
        {
          dataField: "currency",
          text: t("Currency"),
          formatter: (val) => getRowCurrency(val),
        },
        {
          dataField: "agent",
          text: t("Agent"),
          formatter: (val) =>
            val.agent ? `${val.agent.firstName} ${val.agent.lastName}` : "-",
        },
      ];
    case "commission":
      return [
        {
          dataField: "customerName",
          text: t("Customer Name"),
          formatter: (val) =>
            val?.customer
              ? (
                <Link
                  to={`/clients/${val?.customer?._id}/profile`}
                >{`${val.customer.firstName} ${val.customer.lastName}`}</Link>
              ) || "-"
              : "-",
        },
        {
          dataField: "clientLogin",
          text: t("AccountLogin"),
        },
        {
          dataField: "profit",
          text: t("Amount"),
        },
        {
          dataField: "action",
          text: t("Action"),
          formatter: (val) => (val.action === 10 ? "Rebate" : "Commission"),
        },
        {
          dataField: "clientDealId",
          text: t("Client Deal"),
        },
        {
          dataField: "clientVolume",
          text: t("Client Volume"),
          formatter: (val) =>
            val.action === 10
              ? val.clientVolumeClosed / 10000
              : val.clientVolume / 10000,
        },
        {
          dataField: "agent",
          text: t("Agent"),
          formatter: (val) =>
            val.agent ? `${val.agent.firstName} ${val.agent.lastName}` : "-",
        },
        {
          dataField: "createdAt",
          text: t("Added in DB"),
          formatter: (val) => formatReportDate(val.createdAt),
        },
      ];
    case "summary":
      return [
        {
          dataField: "name",
          text: t("Customer Name"),
          formatter: (val) =>
            val?.customer
              ? (
                <Link
                  to={`/clients/${val?.customer?._id}/profile`}
                >{`${val?.customer?.firstName} ${val?.customer?.lastName}`}</Link>
              ) || "-"
              : "-",
        },
        {
          dataField: "name",
          text: t("IB Name"),
          formatter: (val) =>
            val?.parent
              ? (
                <Link
                  to={`/clients/${val?.parent?._id}/profile`}
                >{`${val?.parent?.firstName} ${val?.parent?.lastName}`}</Link>
              ) || "-"
              : "-",
        },
        {
          dataField: "login",
          text: t("AccountLogin"),
        },
        {
          dataField: "dealId",
          text: t("Deal Id"),
        },
        {
          dataField: "positionId",
          text: t("Position Id"),
        },
        {
          dataField: "openPrice",
          text: t("Price Open"),
        },
        {
          dataField: "closePrice",
          text: t("Price Close"),
        },
        {
          dataField: "lots",
          text: t("Volume"),
          formatter: (val) => val.volume / 10000,
        },
        {
          dataField: "profit",
          text: t("Amount"),
        },
        {
          dataField: "agent",
          text: t("Agent"),
          formatter: (val) =>
            val.agent ? `${val.agent.firstName} ${val.agent.lastName}` : "-",
        },
        {
          dataField: "createdAt",
          text: t("Traded Time"),
          formatter: (val) => formatDateBroker(val.time * 1000),
        },
      ];
    case "lead-converted":
      return [
        {
          dataField: "name",
          text: t("Customer Name"),
          formatter: (val) => `${val.firstName} ${val.lastName}`,
        },
        {
          dataField: "agent",
          text: t("Agent"),
          formatter: (val) =>
            val.agent ? `${val.agent.firstName} ${val.agent.lastName}` : "",
        },
        {
          dataField: "convertTime",
          text: t("Convert Time"),
          formatter: (val) => formatReportDate(val?.fx?.demoConvertTime),
        },
        {
          dataField: "createdAt",
          text: t("Added in DB"),
          formatter: (val) => formatReportDate(val.createdAt),
        },
      ];
    case "lead-call-status":
      return [
        {
          dataField: "name",
          text: t("Customer Name"),
          formatter: (val) => `${val.firstName} ${val.lastName}`,
        },
        {
          dataField: "agent",
          text: t("Agent"),
          formatter: (val) =>
            val.agent ? `${val.agent.firstName} ${val.agent.lastName}` : "",
        },
        {
          dataField: "callStatus",
          text: t("Call Status"),
        },
        {
          dataField: "createdAt",
          text: t("Added in DB"),
          formatter: (val) => formatReportDate(val.createdAt),
        },
      ];
    case "last-login":
      return [
        {
          dataField: "name",
          text: t("Customer Name"),
          formatter: (val) => `${val.firstName} ${val.lastName}`,
        },
        {
          dataField: "agent",
          text: t("Agent"),
          formatter: (val) =>
            val.agent ? `${val.agent.firstName} ${val.agent.lastName}` : "-",
        },
        {
          dataField: "lastLogin",
          text: t("Last Login"),
          formatter: (val) => formatReportDate(val.lastLogin),
        },
        {
          dataField: "createdAt",
          text: t("Added in DB"),
          formatter: (val) => formatReportDate(val.createdAt),
        },
      ];
    case "unfunded-accounts":
      return [
        {
          dataField: "login",
          text: t("AccountLogin"),
          formatter: (val) => <>{val?.login}</>,
        },
        {
          dataField: "clientName",
          text: t("Customer Name"),
          formatter: (val) => (
            <Link to={`clients/${val?.customer?._id}/profile`}>
              {val?.customer
                ? `${val?.customer?.firstName} ${val?.customer?.lastName}`
                : ""}
            </Link>
          ),
        },
        {
          dataField: "currency",
          text: t("Currency"),
          formatter: (val) => getRowCurrency(val),
        },
        {
          dataField: "agent",
          text: t("Agent"),
          formatter: (val) =>
            val?.agent?.length
              ? `${val.agent?.[0]?.firstName} ${val.agent?.[0]?.lastName}`
              : "-",
        },
        {
          dataField: "createdAt",
          text: t("Added in DB"),
          formatter: (val) => formatReportDate(val.createdAt),
        },
      ];
    case "credit-in":
    case "credit-out":
      return [
        {
          dataField: "login",
          text: t("AccountLogin"),
          formatter: (val) => <>{val?.login ? val?.login : ""}</>,
        },
        {
          dataField: "clientName",
          text: t("Customer Name"),
          formatter: (val) => (
            <Link to={`clients/${val?.customer?._id}/profile`}>
              {val?.customer
                ? `${val?.customer?.firstName} ${val?.customer?.lastName}`
                : ""}
            </Link>
          ),
        },
        {
          dataField: "amount",
          text: t("Amount"),
          formatter: (val) => formatAmountValue(val?.amount),
        },
        {
          dataField: "agent",
          text: t("Agent"),
          formatter: (val) =>
            val?.agent ? `${val.agent?.firstName} ${val.agent?.lastName}` : "-",
        },
        {
          dataField: "createdAt",
          text: t("Added in DB"),
          formatter: (val) => formatReportDate(val.createdAt),
        },
      ];
    case "ib-summary":
      return [
        {
          dataField: "client",
          text: t("Customer Name"),
          formatter: (val) =>
            val?.customer ? (
              <Link to={`clients/${val?.customer?._id}/profile`}>
                {val?.customer
                  ? `${val?.customer?.firstName} ${val?.customer?.lastName}`
                  : "-"}
              </Link>
            ) : (
              "-"
            ),
        },
        {
          dataField: "lotsOpened",
          text: t("Commission for Lots Opened"),
          formatter: (val) => val?.lotsOpened / 10000,
        },
        {
          dataField: "commission",
          text: t("Commission"),
        },
        {
          dataField: "lotsClosed",
          text: t("Rebate for Lots Closed"),
          formatter: (val) => val?.lotsClosed / 10000,
        },
        {
          dataField: "rebate",
          text: t("Rebate"),
        },
        {
          dataField: "agent",
          text: t("Agent"),
          formatter: (val) =>
            val?.agent ? `${val.agent?.firstName} ${val.agent?.lastName}` : "-",
        },
      ];

    case "ib-total-closed-lots-by-product":
      return [
        {
          dataField: "ibName",
          text: t("IB Name"),
        },
        {
          dataField: "clientName",
          text: t("Client Name"),
        },
        {
          dataField: "login",
          text: t("Client Login / Account ID"),
        },
        {
          dataField: "accountType",
          text: t("Account Type"),
        },
        {
          dataField: "productType",
          text: t("Product Type"),
        },
        {
          dataField: "closedLots",
          text: t("Closed Lots"),
          formatter: (val) => Number(val?.closedLots ?? 0).toFixed(4),
        },
        {
          dataField: "openedLots",
          text: t("Opened Lots"),
          formatter: (val) => Number(val?.openedLots ?? 0).toFixed(4),
        },
        {
          dataField: "rebateAmount",
          text: t("Rebate Amount"),
          formatter: (val) => Number(val?.rebateAmount ?? 0).toFixed(2),
        },
        {
          dataField: "commissionAmount",
          text: t("Commission Amount"),
          formatter: (val) => Number(val?.commissionAmount ?? 0).toFixed(2),
        },
        {
          dataField: "tradeProfit",
          text: t("PnL"),
          formatter: (val) => Number(val?.tradeProfit ?? 0).toFixed(2),
        },
      ];

    case "ib-total-closed-lots-by-symbol":
      return [
        {
          dataField: "ibName",
          text: t("IB Name"),
        },
        {
          dataField: "clientName",
          text: t("Client Name"),
        },
        {
          dataField: "login",
          text: t("Client Login / Account ID"),
        },
        {
          dataField: "accountType",
          text: t("Account Type"),
        },
        {
          dataField: "symbol",
          text: t("Symbol"),
        },
        {
          dataField: "closedLots",
          text: t("Closed Lots"),
          formatter: (val) => Number(val?.closedLots ?? 0).toFixed(4),
        },
        {
          dataField: "openedLots",
          text: t("Opened Lots"),
          formatter: (val) => Number(val?.openedLots ?? 0).toFixed(4),
        },
        {
          dataField: "rebateAmount",
          text: t("Rebate Amount"),
          formatter: (val) => Number(val?.rebateAmount ?? 0).toFixed(2),
        },
        {
          dataField: "commissionAmount",
          text: t("Commission Amount"),
          formatter: (val) => Number(val?.commissionAmount ?? 0).toFixed(2),
        },
        {
          dataField: "tradeProfit",
          text: t("PnL"),
          formatter: (val) => Number(val?.tradeProfit ?? 0).toFixed(2),
        },
      ];

    case "ib-referrals":
      return [
        {
          dataField: "firstName",
          text: t("IB Name"),
          formatter: (val) =>
            `${val?.firstName || "-"} ${val?.lastName || ""}`.trim(),
        },
        {
          dataField: "email",
          text: t("Email Address"),
        },
        {
          dataField: "numClients",
          text: t("Num of Clients"),
          formatter: (val) => val?.numClients ?? 0,
        },
        {
          dataField: "totalDeposits",
          text: t("Total Deposits"),
          formatter: (val) => parseFloat(val?.totalDeposits || 0).toFixed(2),
        },
        {
          dataField: "totalWithdrawals",
          text: t("Total Withdrawals"),
          formatter: (val) => parseFloat(val?.totalWithdrawals || 0).toFixed(2),
        },
      ];

    case "wallet-transactions":
      return [
        {
          dataField: "createdAt",
          text: t("Date"),
          formatter: (val) => formatDateBroker(val.createdAt),
        },
        {
          dataField: "clientType",
          text: t("Client Type"),
          formatter: () => t("Individual"),
        },
        {
          dataField: "customerId",
          text: t("Client Name"),
          formatter: (val) =>
            val.customerId
              ? `${captilazeFirstLetter(val.customerId.firstName)} ${captilazeFirstLetter(val.customerId.lastName)}`
              : "-",
        },
        {
          dataField: "masterIb",
          text: t("Master IB"),
          formatter: (val) => val?.masterIb ?? val?.masterIB ?? "-",
        },
        {
          dataField: "type",
          text: t("Type"),
          formatter: (val) => {
            const labels = {
              DEPOSIT: t("Deposit"),
              WITHDRAW: t("Withdrawal"),
              TRANSFER: t("Internal Transfer"),
              CASHBACK: t("Cashback"),
            };
            return labels[val.type] || val.type || "-";
          },
        },
        {
          dataField: "wallet",
          text: t("Wallet"),
          formatter: (val) => val.wallet || "-",
        },
        {
          dataField: "gateway",
          text: t("Gateway"),
          formatter: (val) => {
            if (!val.gateway || val.gateway === "-") return "-";
            if (val.gateway === "INTERNAL_TRANSFER") return t("Internal");
            if (val.gateway === "WIRE_TRANSFER") return "Wire";
            return val.gateway.split("_").join(" ").toLowerCase();
          },
        },
        {
          dataField: "amount",
          text: t("Amount"),
          formatter: (val) => formatAmountValue(val?.amount),
        },
        {
          dataField: "status",
          text: t("Status"),
          formatter: (val) => <Badge status={val.status} />,
        },
      ];

    case "wallet-balance":
      return [
        {
          dataField: "customerId",
          text: t("Client Name"),
          formatter: (val) =>
            val?.customerId ? (
              <Link to={`/clients/${val.customerId}/profile`}>
                {`${captilazeFirstLetter(val.firstName || "")} ${captilazeFirstLetter(val.lastName || "")}`}
              </Link>
            ) : (
              "-"
            ),
        },
        {
          dataField: "clientType",
          text: t("Client Type"),
          formatter: (val) => val?.clientType || "-",
        },
        {
          dataField: "email",
          text: t("Email"),
          formatter: (val) => val?.email || "-",
        },
        {
          dataField: "currency",
          text: t("Currency"),
          formatter: (val) => val?.currency || "-",
        },
        {
          dataField: "walletType",
          text: t("Wallet Type"),
          formatter: (val) => {
            const wt = val?.walletType || "Standard";
            const cls = wt === "IB" ? "badge badge-soft-warning" : "badge badge-soft-secondary";
            return <span className={cls}>{t(wt)}</span>;
          },
        },
        {
          dataField: "opening",
          text: t("Opening Balance"),
          formatter: (val) =>
            val?.opening != null ? Number(val.opening).toFixed(2) : "-",
        },
        {
          dataField: "openingDeposits",
          text: t("Opening Deposits"),
          formatter: (val) => formatSignedMoney(val?.openingDeposits, "positive"),
        },
        {
          dataField: "openingWithdraws",
          text: t("Opening Withdrawals"),
          formatter: (val) => formatSignedMoney(val?.openingWithdraws, "negative"),
        },
        {
          dataField: "openingTransfersIn",
          text: t("Opening Transfers In"),
          formatter: (val) => formatSignedMoney(val?.openingTransfersIn, "positive"),
        },
        {
          dataField: "openingTransfersOut",
          text: t("Opening Transfers Out"),
          formatter: (val) => formatSignedMoney(val?.openingTransfersOut, "negative"),
        },
        {
          dataField: "openingCashbacks",
          text: t("Opening Cashbacks"),
          formatter: (val) => formatSignedMoney(val?.openingCashbacks, "positive"),
        },
        {
          dataField: "openingRebateCommission",
          text: t("Opening Rebate & Commission"),
          formatter: (val) => formatSignedMoney(val?.openingRebateCommission, "positive"),
        },
        {
          dataField: "closing",
          text: t("Closing Balance"),
          formatter: (val) =>
            val?.closing != null ? Number(val.closing).toFixed(2) : "-",
        },
        {
          dataField: "netChange",
          text: t("Net Change"),
          formatter: (val) => {
            if (val?.netChange == null) return "-";
            const n = Number(val.netChange);
            const cls = n > 0 ? "text-success" : n < 0 ? "text-danger" : "";
            return (
              <span className={cls}>
                {n > 0 ? "+" : ""}
                {n.toFixed(2)}
              </span>
            );
          },
        },
        {
          dataField: "deposits",
          text: t("Deposits"),
          formatter: (val) => formatSignedMoney(val?.deposits, "positive"),
        },
        {
          dataField: "withdraws",
          text: t("Withdrawals"),
          formatter: (val) => formatSignedMoney(val?.withdraws, "negative"),
        },
        {
          dataField: "transfersIn",
          text: t("Transfers In"),
          formatter: (val) => formatSignedMoney(val?.transfersIn, "positive"),
        },
        {
          dataField: "transfersOut",
          text: t("Transfers Out"),
          formatter: (val) => formatSignedMoney(val?.transfersOut, "negative"),
        },
        {
          dataField: "cashbacks",
          text: t("Cashbacks"),
          formatter: (val) => formatSignedMoney(val?.cashbacks, "positive"),
        },
        {
          dataField: "rebateCommission",
          text: t("Rebate & Commission"),
          formatter: (val) => formatSignedMoney(val?.rebateCommission, "positive"),
        },
        {
          dataField: "lastActivity",
          text: t("Last Activity"),
          formatter: (val) => val?.lastActivityDisplay || "-",
        },
      ];
    case "forex-deposit-details":
      return [
        {
          dataField: "customerId",
          text: t("Client Name"),
          formatter: (val) =>
            val?.customerId ? (
              <Link to={`/clients/${val.customerId}/profile`}>
                {`${captilazeFirstLetter(val.firstName || "")} ${captilazeFirstLetter(val.lastName || "")}`}
              </Link>
            ) : (
              "-"
            ),
        },
        {
          dataField: "clientType",
          text: t("Client Type"),
          formatter: (val) => val?.clientType || "-",
        },
        {
          dataField: "email",
          text: t("Email"),
          formatter: (val) => val?.email || "-",
        },
        {
          dataField: "currency",
          text: t("Currency"),
          formatter: (val) => val?.currency || "-",
        },
        {
          dataField: "walletType",
          text: t("Wallet Type"),
          formatter: (val) => {
            const wt = val?.walletType || "Standard";
            const cls = wt === "IB" ? "badge badge-soft-warning" : "badge badge-soft-secondary";
            return <span className={cls}>{t(wt)}</span>;
          },
        },
        {
          dataField: "balanceNow",
          text: t("Balance Now"),
          formatter: (val) => formatAmountValue(val?.balanceNow),
        },
        {
          dataField: "closing",
          text: t("Closing Balance"),
          formatter: (val) => formatAmountValue(val?.closing),
        },
        {
          dataField: "deposits",
          text: t("Deposits"),
          formatter: (val) => formatSignedMoney(val?.deposits, "positive"),
        },
        {
          dataField: "withdraws",
          text: t("Withdrawals"),
          formatter: (val) => formatSignedMoney(val?.withdraws, "negative"),
        },
        {
          dataField: "transfersIn",
          text: t("Transfers In"),
          formatter: (val) => formatSignedMoney(val?.transfersIn, "positive"),
        },
        {
          dataField: "transfersOut",
          text: t("Transfers Out"),
          formatter: (val) => formatSignedMoney(val?.transfersOut, "negative"),
        },
        {
          dataField: "cashbacks",
          text: t("Cashbacks"),
          formatter: (val) => formatSignedMoney(val?.cashbacks, "positive"),
        },
        {
          dataField: "ibEarnings",
          text: t("IB Earnings"),
          formatter: (val) => formatSignedMoney(val?.ibEarnings, "positive"),
        },
        {
          dataField: "adjustments",
          text: t("Adjustments"),
          formatter: (val) => formatActualSignedMoney(val?.adjustments),
        },
      ];
    case "payment-gateway":
      return [
        {
          dataField: "recordId",
          text: t("Transaction ID"),
        },
        {
          dataField: "createdAt",
          text: t("Creation Date"),
          formatter: (val) => formatDateBroker(val.createdAt),
        },
        {
          dataField: "approvalDate",
          text: t("Approval / Reject Date"),
          formatter: (val) =>
            val.approvalDate ? formatDateBroker(val.approvalDate) : "-",
        },
        {
          dataField: "customerId",
          text: t("Client Name"),
          formatter: (val) =>
            val.customerId
              ? `${captilazeFirstLetter(val.customerId.firstName)} ${captilazeFirstLetter(val.customerId.lastName)}`
              : "-",
        },
        {
          dataField: "clientEmail",
          text: t("Client Email"),
          formatter: (val) => val.customerId?.email || "-",
        },
        {
          dataField: "clientType",
          text: t("Client Type"),
          formatter: () => t("Individual"),
        },
        {
          dataField: "masterIb",
          text: t("Master IB"),
          formatter: (val) => val?.masterIb ?? val?.masterIB ?? "-",
        },
        {
          dataField: "accountId",
          text: t("Trading Account / Wallet ID"),
          formatter: (val) => val.login || val.wallet?.toString() || "-",
        },
        {
          dataField: "transactionType",
          text: t("Transaction Type"),
          formatter: (val) => val.transactionType || "-",
        },
        {
          dataField: "accountSource",
          text: t("Account Source"),
          formatter: (val) => val.accountSource || "-",
        },
        {
          dataField: "gateway",
          text: t("Payment Gateway"),
          formatter: (val) =>
            val.gateway === "WIRE_TRANSFER"
              ? "Wire"
              : val.gateway?.split("_").join(" ")?.toLowerCase() || "-",
        },
        {
          dataField: "currency",
          text: t("Currency"),
          formatter: (val) => getRowCurrency(val),
        },
        {
          dataField: "amount",
          text: t("Paid Amount"),
          formatter: (val) => formatAmountValue(val?.amount),
        },
        {
          dataField: "status",
          text: t("Status"),
          formatter: (val) => <Badge status={val.status} />,
        },
        {
          dataField: "agent",
          text: t("Agent / Sales User"),
          formatter: (val) =>
            val.agent ? `${val.agent.firstName} ${val.agent.lastName}` : "-",
        },
      ];
    case "sales-commision":
      return [
        {
          dataField: "recordId",
          text: t("Transaction Id"),
        },
        {
          dataField: "name",
          text: t("Agent"),
          formatter: (val) => val?.userId ? `${val?.userId?.firstName} ${val?.userId?.lastName}` : "-"
        },
        {
          dataField: "type",
          text: t("Type"),
          formatter: (val) => val?.type,
        },
        {
          dataField: "amount",
          text: t("Amount"),
          formatter: (val) => formatAmountValue(val?.amount),
        },
        {
          dataField: "currency",
          text: t("Currency"),
          formatter: (val) => getRowCurrency(val),
        },
        {
          dataField: "status",
          text: t("Status"),
          formatter: (val) => <Badge isCentered={true} status={val.status}></Badge>,
        },
      ];
    default:
      return [
        {
          dataField: "login",
          text: "Login",
        },
        {
          dataField: "profit",
          text: "Amount",
        },
        {
          dataField: "comment",
          text: "Comment",
        },
        {
          dataField: "createdAt",
          text: "Added in DB",
          formatter: (val) => formatReportDate(val.createdAt),
        },
      ];
  }
}

function ShowStats(props) {
  const {
    type, dateFrom, dateTo, agent, ibId, productType, accountType, customerId, grandTotal, login, client, status, gateway, symbol, clientType, customerIds, search
  } = props;
  if (!type) return <></>;
  const [loading, setLoading] = useState(false);
  const [stats, setStats] = useState({});

  const skipStatsApi = ["client-deposits", "client-withdrawals", "wallet-balance", "forex-deposit-details"].includes(type);

  useEffect(() => {
    if (skipStatsApi) {
      setStats({});
      return;
    }
    setLoading(true);
    if (type) {
      const tzOffset = -new Date().getTimezoneOffset();
      (async () => {
        axiosHelper
          .get(`/reports/stats/${type}`, {
            params: {
              dateFrom,
              dateTo,
              agent,
              ibId,
              status,
              gateway,
              productType,
              accountType,
              customerId,
              login,
              client,
              symbol,
              clientType,
              customerIds,
              search,
              tzOffset
            },
          })
          .then((res) => {
            setStats(res?.result?.[0]);
            setLoading(false);
          })
          .catch((err) => {
            setLoading(false);
            // eslint-disable-next-line no-console
            console.log(err);
          });
      })();
    }
  }, [type, dateFrom, dateTo, agent, ibId, productType, accountType, customerId, skipStatsApi, status, gateway, login, client, symbol, clientType, customerIds, search]);

  if (skipStatsApi) {
    if (!grandTotal) return <></>;
    if (type === "client-deposits") {
      return <>FX Deposit: {parseFloat(grandTotal.fxSum || 0).toFixed(2)} | Wallet Deposit: {parseFloat(grandTotal.walletSum || 0).toFixed(2)} | Total: {parseFloat(grandTotal.total || 0).toFixed(2)} USD</>;
    }
    if (type === "client-withdrawals") {
      return <>FX Withdrawal: {parseFloat(grandTotal.fxSum || 0).toFixed(2)} | Wallet Withdrawal: {parseFloat(grandTotal.walletSum || 0).toFixed(2)} | Total: {parseFloat(grandTotal.total || 0).toFixed(2)} USD</>;
    }
    if (type === "wallet-balance") {
      const currencySummary = Object.entries(grandTotal.byCurrency || {})
        .map(([c, b]) => `${c}: ${parseFloat(b.closing || 0).toFixed(2)}`)
        .join(" | ");
      const walletTypeSummary = Object.entries(grandTotal.byWalletType || {})
        .map(([w, b]) => `${w}: ${parseFloat(b.closing || 0).toFixed(2)}`)
        .join(" | ");
      return (
        <>
          Clients: {grandTotal.totalClients || 0} | Rows: {grandTotal.totalRows || 0}
          {" | "}Opening: {parseFloat(grandTotal.totalOpening || 0).toFixed(2)}
          {" | "}Closing: {parseFloat(grandTotal.totalClosing || 0).toFixed(2)}
          {" | "}Net: {parseFloat(grandTotal.totalNetChange || 0).toFixed(2)}
          {" | "}Deposits: {parseFloat(grandTotal.totalDeposits || 0).toFixed(2)}
          {" | "}Withdraws: {parseFloat(grandTotal.totalWithdraws || 0).toFixed(2)}
          {" | "}Cashbacks: {parseFloat(grandTotal.totalCashbacks || 0).toFixed(2)}
          {" | "}Rebate & Commission: {parseFloat(grandTotal.totalRebateCommission || 0).toFixed(2)}
          {currencySummary ? ` | ${currencySummary}` : ""}
          {walletTypeSummary ? ` | Wallet Type - ${walletTypeSummary}` : ""}
        </>
      );
    }

    if (type === "forex-deposit-details") return <WalletReportSummary grandTotal={grandTotal} />;
  }

  if (!Object.keys(stats || {}).length) return <></>;

  if (loading) return <Spinner />;

  const formatLots = (lots) => Number(lots) / 10000;

  const formatData = (data) => {
    switch (type) {
      case "deposits":
        return `Total Deposit : ${parseFloat(
          data?.totalDeposit?.$numberDecimal || data?.totalDeposit || 0
        )?.toFixed(2)} ${data.currency}`;
      case "withdrawals":
        return `Total Withdrawal : ${parseFloat(
          data?.totalWithdrawal?.$numberDecimal || data?.totalWithdrawal || 0
        )?.toFixed(2)} ${data.currency}`;
      case "client-deposits":
        return `FX Deposit: ${parseFloat(data?.totalFxSum || 0).toFixed(2)} | Wallet Deposit: ${parseFloat(data?.totalWalletSum || 0).toFixed(2)} | Total: ${parseFloat(data?.totalDeposit || 0).toFixed(2)} USD`;
      case "client-withdrawals":
        return `FX Withdrawal: ${parseFloat(data?.totalFxSum || 0).toFixed(2)} | Wallet Withdrawal: ${parseFloat(data?.totalWalletSum || 0).toFixed(2)} | Total: ${parseFloat(data?.totalWithdrawal || 0).toFixed(2)} USD`;
      case "deposit-status-fx":
        return `Total FX Deposits : ${parseFloat(data?.totalFxSum || 0).toFixed(2)}  USD`;
      case "withdrawals-status-fx":
        return `Total FX Withdrawals : ${parseFloat(data?.totalFxSum || 0).toFixed(2)}  USD`;
      case "deposit-status":
        return `Total Wallet Deposits : ${parseFloat(data?.totalSum || 0).toFixed(2)}  USD`;
      case "withdrawals-status":
        return `Total Wallet Withdrawals : ${parseFloat(data?.totalSum || 0).toFixed(2)}  USD`;
      case "credit-in":
        return `Total Amount : ${data?.totalCreditAmount || 0}`;
      case "credit-out":
        return `Total Amount : ${data?.totalCreditAmount || 0}`;
      case "ib-summary":
        return `Total Commission : ${data?.totalCommission || 0} | Total Rebate : ${data?.totalRebate || 0} | Total Lots Opened : ${formatLots(data?.totalLotsOpened) || 0} | Total Lots Closed : ${formatLots(data?.totalLotsClosed) || 0}`;
      case "ib-total-closed-lots-by-product":
        return `Total Closed Lots : ${Number(data?.totalClosedLots || 0).toFixed(4)} | Total Opened Lots : ${Number(data?.totalOpenedLots || 0).toFixed(4)} | Total Rebate Amount : ${Number(data?.totalRebateAmount || 0).toFixed(2)} | Total Commission Amount : ${Number(data?.totalCommissionAmount || 0).toFixed(2)} | Total Trade Profit : ${Number(data?.totalTradeProfit || 0).toFixed(2)}`;
      case "ib-total-closed-lots-by-symbol":
        return `Total Closed Lots : ${Number(data?.totalClosedLots || 0).toFixed(4)} | Total Opened Lots : ${Number(data?.totalOpenedLots || 0).toFixed(4)} | Total Rebate Amount : ${Number(data?.totalRebateAmount || 0).toFixed(2)} | Total Commission Amount : ${Number(data?.totalCommissionAmount || 0).toFixed(2)} | Total Trade Profit : ${Number(data?.totalTradeProfit || 0).toFixed(2)}`;
      default:
        return "";
    }
  };

  return <>{formatData(stats)}</>;
}

function ReportsList(props) {
  const dispatch = useDispatch();
  const [columns, setColumns] = useState([]);
  const [payload, setPayload] = useState({});
  const { i18n, t } = useTranslation();
  useEffect(() => {
    const params = {
      limit: 1000,
      page: 1,
    };
    dispatch(getSalesAgentsStart(params));
    dispatch(fetchTradingAccounts(params));
    dispatch(fetchClientsStart({
      isUnlimited: true,
    }));
  }, []);
  return (
    <React.Fragment>
      <MetaTags>
        <title>Reports</title>
      </MetaTags>
      <div className="page-content">
        <div className="container-fluid">
          <h2>{t("Reports")}</h2>
          <Row>
            <Col className="col-12">
              <Card>
                <CardHeader className="d-flex justify-content-between  align-items-center">
                  <CardTitle className="color-primary">{t("Reports")}</CardTitle>
                </CardHeader>
                <CardBody>
                  <ReportForm
                    language={i18n.language}
                    setReportColumns={(type) => {
                      setColumns(getColumns(type, t));
                    }}
                    setPayload={setPayload}
                  >
                    <Table
                      id="tech-companies-1"
                      className="table  table-hover "
                    >
                      <Thead className="text-center table-light">
                        <Tr>
                          {columns.map((column, index) => (
                            <Th data-priority={index} key={index}>
                              {column.text}
                            </Th>
                          ))}
                        </Tr>
                      </Thead>
                      <Tbody
                        className="text-center"
                        style={{ fontSize: "13px" }}
                      >
                        {props.loading && <TableLoader colSpan={4} />}
                        {!props.loading && props.docs.length === 0 && (
                          <>
                            <Tr
                              style={{
                                height: "50vh",
                              }}
                            >
                              <Td
                                colSpan={"100%"}
                                className="fw-bolder text-center"
                              >
                                {props.error ? (
                                  <div className="text-danger" role="alert">{props.error}</div>
                                ) : props.requiresExport ? (
                                  <div className="d-flex flex-column align-items-center justify-content-center">
                                    <i className="bx bx-mail-send" style={{ fontSize: "2.5rem" }} />
                                    <h4 className="fw-bolder text-center mt-2">
                                      {t(props.exportMessage || "This report covers all clients and is too large to display here. Use “Download Report” or “Send As Email” to receive it by email.")}
                                    </h4>
                                  </div>
                                ) : (
                                  <h3 className="fw-bolder text-center">
                                    {t("No records")}
                                  </h3>
                                )}
                              </Td>
                            </Tr>
                          </>
                        )}
                        {!props.loading &&
                          props.docs.map((row, rowIndex) => (<Tr key={rowIndex}>
                            {columns.map((column, index) => (
                              <Td key={`${rowIndex}-${index}`}>
                                {column.formatter
                                  ? column.formatter(row, rowIndex)
                                  : (() => {
                                    const val = row[column.dataField];
                                    // ✅ Never render objects directly
                                    if (val === null || val === undefined) return "-";
                                    if (typeof val === "object") return JSON.stringify(val);
                                    return val;
                                  })()
                                }
                              </Td>
                            ))}
                          </Tr>)
                          )
                        }

                        <Tr>
                          <Td colSpan={columns.length}>
                            <ShowStats
                              type={payload?.type}
                              dateFrom={payload?.dateFrom}
                              dateTo={payload?.dateTo}
                              agent={payload?.agent}
                              ibId={payload?.ibId}
                              productType={payload?.productType}
                              customerId={payload?.customerId}
                              status={payload?.status}
                              gateway={payload?.gateway}
                              symbol={payload?.symbol}
                              grandTotal={props.grandTotal}
                              login={payload?.login}
                              client={payload?.client}
                              clientType={payload?.clientType}
                              customerIds={payload?.customerIds}
                              search={payload?.search}
                            />
                          </Td>
                        </Tr>
                      </Tbody>
                      {["client-deposits", "client-withdrawals"].includes(payload?.type) && props.grandTotal && !props.loading && props.docs.length > 0 && (
                        <tfoot>
                          <tr style={{
                            fontWeight: "bold",
                            backgroundColor: "#f8f9fa",
                          }}>
                            <td className="py-2 text-center" colSpan={4}>{t("Grand Total")}</td>
                            <td className="py-2 text-center">{parseFloat(props.grandTotal.total || 0).toFixed(2)}</td>
                            <td className="py-2 text-center">USD</td>
                            <td className="py-2 text-center" colSpan={2}></td>
                          </tr>
                        </tfoot>
                      )}
                      {payload?.type === "ib-referrals" && props.grandTotal && !props.loading && props.docs.length > 0 && (
                        <tfoot>
                          <tr style={{
                            fontWeight: "bold",
                            backgroundColor: "#f8f9fa",
                          }}>
                            <td className="py-2 text-center" colSpan={3}>{t("Grand Total")}</td>
                            <td className="py-2 text-center">{parseFloat(props.grandTotal.totalDeposits || 0).toFixed(2)}</td>
                            <td className="py-2 text-center">{parseFloat(props.grandTotal.totalWithdrawals || 0).toFixed(2)}</td>
                          </tr>
                        </tfoot>
                      )}
                      {payload?.type === "daily-net-deposit" && props.grandTotal && !props.loading && props.docs.length > 0 && (
                        <tfoot>
                          <tr style={{
                            fontWeight: "bold",
                            backgroundColor: "#f8f9fa",
                          }}>
                            <td className="py-2 text-center" colSpan={2}>{t("Grand Total")}</td>
                            <td className="py-2 text-center color-green">{parseFloat(props.grandTotal.totalFxDeposits || 0).toFixed(2)}</td>
                            <td className="py-2 text-center color-green">{parseFloat(props.grandTotal.totalWalletDeposits || 0).toFixed(2)}</td>
                            <td className="py-2 text-center color-red">{parseFloat(props.grandTotal.totalFxWithdrawals || 0).toFixed(2)}</td>
                            <td className="py-2 text-center color-red">{parseFloat(props.grandTotal.totalWalletWithdrawals || 0).toFixed(2)}</td>
                            <td className="py-2 text-center color-green">{parseFloat(props.grandTotal.grandTotalDeposited || 0).toFixed(2)}</td>
                            <td className="py-2 text-center color-red">{parseFloat(props.grandTotal.grandTotalWithdrawn || 0).toFixed(2)}</td>
                            <td className={`py-2 text-center ${parseFloat(props.grandTotal.grandTotalNetMoneyFlow || 0) > 0 ? "color-green" : "color-red"}`}>
                              {parseFloat(props.grandTotal.grandTotalNetMoneyFlow || 0).toFixed(2)}
                            </td>
                            <td className="py-2 text-center">{props.grandTotal.currency || "-"}</td>
                          </tr>
                        </tfoot>
                      )}
                      {payload?.type === "wallet-transactions" && props.typeTotals && !props.loading && props.docs.length > 0 && (() => {
                        const typeRows = [
                          {
                            key: "DEPOSIT",
                            label: t("Total Deposit")
                          },
                          {
                            key: "WITHDRAW",
                            label: t("Total Withdrawal")
                          },
                          {
                            key: "TRANSFER_IN",
                            label: t("Total Transfer In")
                          },
                          {
                            key: "TRANSFER_OUT",
                            label: t("Total Transfer Out")
                          },
                          {
                            key: "CASHBACK",
                            label: t("Total Cashback")
                          },
                        ];
                        const grandTotalAmt = typeRows.reduce(
                          (s, r) => s + parseFloat(props.typeTotals[r.key] || 0), 0
                        );
                        return (
                          <tfoot>
                            {typeRows.map(({ key, label }) => (
                              <tr key={key} style={{
                                fontWeight: "bold",
                                backgroundColor: "#f8f9fa"
                              }}>
                                <td colSpan={2} />
                                <td className="py-2 text-center">{label}</td>
                                <td colSpan={2} />
                                <td className="py-2 text-center">
                                  {parseFloat(props.typeTotals[key] || 0).toFixed(2)}
                                </td>
                                <td />
                              </tr>
                            ))}
                            <tr style={{
                              fontWeight: "bold",
                              backgroundColor: "#e9ecef",
                              borderTop: "2px solid #dee2e6"
                            }}>
                              <td colSpan={2} />
                              <td className="py-2 text-center">{t("Grand Total")}</td>
                              <td colSpan={2} />
                              <td className="py-2 text-center">{grandTotalAmt.toFixed(2)}</td>
                              <td />
                            </tr>
                          </tfoot>
                        );
                      })()}
                      {payload?.type === "payment-gateway" && !props.loading && props.docs.length > 0 && (
                        <tfoot>
                          <tr style={{
                            backgroundColor: "#e9ecef",
                            borderTop: "2px solid #dee2e6"
                          }}>
                            <td colSpan={2} />
                            <td colSpan={3} className="py-2 text-center">{t("Grand Total")}</td>
                            <td colSpan={2} />
                            <td className="py-2 text-center">
                              {parseFloat(props.grandTotal || 0).toFixed(2)}
                            </td>
                            <td colSpan={5}/>
                          </tr>
                        </tfoot>
                      )}
                    </Table>
                  </ReportForm>
                  {payload?.type === "forex-deposit-details" && props.metadata && !props.loading && (
                    <div className="mt-2 text-muted">
                      <div>{t("Balance cutoff (exclusive)")}: {formatDateBroker(props.metadata.cutoff)} UTC+3</div>
                      <div>{t(props.metadata.dateAccuracy)}</div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
}

const mapStateToProps = (state) => ({
  loading: state.reportsReducer.loading || false,
  docs: state.reportsReducer.docs || [],
  reportsReducer: state.reportsReducer || {},
  grandTotal: state.reportsReducer.grandTotal || state.reportsReducer.grandTotalAmt || null,
  typeTotals: state.reportsReducer.typeTotals || null,
  requiresExport: state.reportsReducer.requiresExport || false,
  exportMessage: state.reportsReducer.message || "",
  error: state.reportsReducer.error || null,
  metadata: state.reportsReducer.metadata || null,
});
export default connect(mapStateToProps, null)(ReportsList);
